import {
  DocumentationModel,
  VersionModel,
} from 'shared/domain/documentation/documentationModel';
import { isUploadStatusSuccess } from 'shared/types/uploadStatus';
import { findCurrentVersion } from './currentVersion';

/** A version whose file can be opened: uploaded, or still only on this device. */
function hasFile(version: VersionModel): boolean {
  return isUploadStatusSuccess(version.uploadStatus) || Boolean(version.localData);
}

/**
 * The version the app shows for a documentation (PT-7117): its current
 * version, not the newest one; while the documentation's file is still being
 * uploaded from this device (it gets its current version once stored), that
 * file. `undefined`: nothing to show (no current version, or its file is
 * not uploaded), the documentation is shown by its name only.
 */
export function findDisplayedVersion(documentation: DocumentationModel): VersionModel | undefined {
  const current = findCurrentVersion(documentation);
  if (current) {
    return hasFile(current) ? current : undefined;
  }
  return documentation.versions.find((version) => Boolean(version.localData) && !isUploadStatusSuccess(version.uploadStatus));
}

/** Whether the documentation has a file to show. */
export function hasDisplayedVersion(documentation: DocumentationModel): boolean {
  return Boolean(findDisplayedVersion(documentation));
}

/**
 * Whether lists, search and browsers show the documentation (PT-7117 mockup):
 * also without a current version (shown as "no current version"), as long as
 * some version has a file. Placeholders whose versions never got a file are
 * left out (they once showed up as thousands of ghost rows).
 */
export function isListedDocumentation(documentation: DocumentationModel): boolean {
  return hasDisplayedVersion(documentation) || documentation.versions.some(hasFile);
}
